/**
 * Which expansion a Pre-Rift (sealed prerelease) is played from.
 *
 * Derived from `SET_RELEASE_CALENDAR` the same way constructed eras are:
 * a new `startsMeta` expansion becomes the Pre-Rift set on its own, with no
 * per-set allowlist. Starters, showcases, and promo catalogs are not Pre-Rifts.
 *
 * During the lead-up to a release the upcoming expansion is current (Radiance
 * the week before Radiance weekend). After release, that expansion stays
 * current until the next one enters its own lead-up.
 */
import { SET_RELEASE_CALENDAR, calendarDayIso } from './setReleaseCalendar.js';

/** Days before release when the upcoming expansion becomes the default Pre-Rift. */
export const PRE_RIFT_LEAD_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

function metaSets() {
  return SET_RELEASE_CALENDAR
    .filter((entry) => entry.startsMeta && entry.releaseDate)
    .slice()
    .sort((a, b) => a.releaseDate.localeCompare(b.releaseDate));
}

function daysUntil(releaseDate, today) {
  const releaseMs = Date.parse(`${releaseDate}T00:00:00.000Z`);
  const todayMs = Date.parse(`${today}T00:00:00.000Z`);
  return Math.round((releaseMs - todayMs) / DAY_MS);
}

/**
 * Expansions a player can log a Pre-Rift for: every released meta set, plus
 * the single next one so the upcoming weekend is selectable before it ships.
 */
export function preRiftSetOptions(today = calendarDayIso()) {
  const sets = metaSets();
  const released = sets.filter((entry) => entry.releaseDate <= today);
  const next = sets.find((entry) => entry.releaseDate > today);
  const codes = released.map((entry) => entry.code);
  if (next) codes.push(next.code);
  return codes;
}

/**
 * Default set for a new Pre-Rift on `today`.
 * The next expansion inside the lead window, otherwise the latest released one.
 */
export function currentPreRiftSetCode(today = calendarDayIso()) {
  const sets = metaSets();
  const next = sets.find((entry) => entry.releaseDate > today);
  if (next && daysUntil(next.releaseDate, today) <= PRE_RIFT_LEAD_DAYS) return next.code;
  const released = sets.filter((entry) => entry.releaseDate <= today);
  return released[released.length - 1]?.code ?? next?.code ?? null;
}
